import { useFormikContext } from 'formik';
import PropTypes from 'prop-types';
import { useCallback, useEffect, useRef } from 'react';
import { Box } from 'rebass/styled-components';

import { Input } from '@/components/Inputs';

const Dropdown = ({ children, isOpen, onOpen, ...props }) => {
  const dropdownRef = useRef();

  const { submitForm, resetForm, dirty, status, setStatus } = useFormikContext();

  const handleClickOutside = useCallback(
    async (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        if (dirty) {
          await submitForm();
        }

        if (status === 'mousedown') {
          onOpen(null);
        }
        resetForm();
      }
    },
    [dirty, onOpen, resetForm, status, submitForm]
  );

  const handleMouseDown = useCallback(() => setStatus('mousedown'), [setStatus]);

  const handleMouseMove = useCallback(
    (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setStatus('dragged');
      }
    },
    [setStatus]
  );

  useEffect(() => {
    document.addEventListener('click', handleClickOutside, true);
    document.addEventListener('mousedown', handleMouseDown, true);
    document.addEventListener('mousemove', handleMouseMove, true);

    return () => {
      document.removeEventListener('click', handleClickOutside, true);
      document.removeEventListener('mousedown', handleMouseDown, true);
      document.removeEventListener('mousemove', handleMouseMove, true);
    };
  }, [handleClickOutside, handleMouseDown, handleMouseMove]);

  return (
    <Box ref={dropdownRef} {...props}>
      {children}
    </Box>
  );
};

Dropdown.propTypes = {
  children: PropTypes.oneOfType([PropTypes.node, PropTypes.shape({}), PropTypes.arrayOf(PropTypes.shape({}))])
    .isRequired,
  isOpen: PropTypes.bool.isRequired,
  onOpen: PropTypes.func.isRequired,
};

Dropdown.Icon = ({ children, onClick, ...props }) => {
  return (
    <Box onClick={onClick} type="button" {...props}>
      {children}
    </Box>
  );
};

Dropdown.Icon.propTypes = {
  children: PropTypes.oneOfType([PropTypes.node, PropTypes.shape({}), PropTypes.arrayOf(PropTypes.shape({}))])
    .isRequired,
  onClick: PropTypes.func,
};

Dropdown.Icon.defaultProps = {
  onClick: undefined,
};

Dropdown.Form = ({ name, title, label, placeholder, type, ...props }) => {
  return <Input name={name} title={title} label={label} placeholder={placeholder} type={type} {...props} />;
};

const defaultProps = PropTypes.oneOfType([PropTypes.string, PropTypes.number]);

Dropdown.Form.propTypes = {
  name: defaultProps.isRequired,
  title: defaultProps.isRequired,
  label: defaultProps.isRequired,
  placeholder: defaultProps.isRequired,
  type: defaultProps.isRequired,
};

export default Dropdown;
